import { toISODate, addDaysIso } from './date.ts'

/**
 * ══ BỘ LỌC + KPI MÀN ĐƠN HÀNG — LUẬT THUẦN DÙNG CHUNG (chép `Az.tsx:2260-2410`) ═════════════════════════════
 *
 * ⭐ PHẦN LỌC TÁCH KHỎI PHẦN VẼ, và đó không phải sở thích: bản cũ có **9 chip lọc nhanh + 15 ô
 * lọc**, và luật của từng cái là một câu hỏi nghiệp vụ riêng ("đơn nào chờ vận hành", "đơn nào
 * còn nợ"). Nhét hết vào thân component thì cách duy nhất để kiểm một luật là vẽ cả màn rồi bấm.
 * Ở đây chúng là hàm thuần, kiểm thẳng được.
 *
 * ⭐ VÌ SAO NẰM Ở `@rs/contracts` (đợt Đ3, 16/10/2026): màn Đơn hàng chuyển sang lọc · đếm · KPI Ở MÁY CHỦ
 *   (`POST /az/orders/bang*`, SQL ở `@rs/order`). Hai bản của cùng một luật — bản JS này và bản SQL — chỉ giữ
 *   được khớp nhau nếu có một bài đối chứng chạy CẢ HAI trên cùng dữ liệu. Bài đó cần CSDL thật nên sống ở
 *   `apps/api`, mà `apps/api` không được đọc mã `apps/web`. Nên bản JS dời về đây — gói duy nhất cả hai đầu
 *   cùng đọc — và `apps/web/.../orders-filter.ts` · `order-kpi.ts` chỉ còn xuất lại. Đây là BẢN GỐC ĐỐI
 *   CHỨNG: sửa luật ở đây mà không sửa SQL (hoặc ngược lại) thì `order-board.test.ts` đỏ.
 *
 * ⚠ ĐẾM CHIP TRÊN TOÀN DANH SÁCH TRONG PHẠM VI, KPI TRÊN TẬP ĐANG LỌC — hai câu hỏi khác nhau, xem từng hàm.
 */

/** Chín chip lọc nhanh, ĐÚNG THỨ TỰ bản cũ (`Az.tsx:2260`). */
export const QUICK_KEYS = [
  'all', 'draft', 'today', 'tomorrow', 'noleg', 'running', 'unbilled', 'owing', 'reefer',
] as const
export type QuickKey = (typeof QUICK_KEYS)[number]

/**
 * Trường NGÀY đem ra lọc — SÁU mốc, đúng thứ tự bản cũ (`Az.tsx:1659`). Bốc / giao THỰC TẾ đọc `actualAt` của điểm lấy đầu /
 * điểm giao cuối, thiếu thì rơi về kế hoạch (`azStopDates`).
 */
export const DATE_BASES = ['planPickup', 'planDeliver', 'actualPickup', 'actualDeliver', 'created', 'invoiced'] as const
export type DateBasis = (typeof DATE_BASES)[number]

/** Nguồn đơn (Đ3): `legacy` đơn hệ RATRACO cũ · `new` đơn rs-erp. Ngoài ô này màn KHÔNG phân biệt cũ/mới. */
export const ORDER_SOURCES = ['', 'legacy', 'new'] as const
export type OrderSource = (typeof ORDER_SOURCES)[number]

/**
 * Chọn hết danh sách đang lọc tối đa bấy nhiêu đơn — bản cũ `AZ_BULK_MAX = 250` (user chốt 11/08, `Az.tsx:1174-1177`), và cũng
 * là TRẦN LÔ của mọi cửa hàng loạt theo đơn (đổi trạng thái · nhả bắn · mở thầu · bắn chặng · xin xuất CT BH).
 * ⚠ Trước 21/10 là 200 (hạ cho bằng trần lô máy chủ) và bắn chặng chỉ 50 ⇒ kế hoạch lặng lẽ cắt còn 50, bấm Bắn ra 400 tiếng
 *   Anh; đổi trạng thái cắt im đơn thứ 201 (rà 21/10 nhap-loat-2 / nhap-loat-4). Nay một trần, đúng số bản cũ, và vượt trần
 *   (tick tay qua nhiều trang) thì báo `BULK_TOO_MANY` tiếng Việt, không cắt.
 * ⚠ KHÔNG dùng làm `limit` của cửa trang (trần `PageQuery` 200) — đọc lại theo `ids` màn chia lô riêng.
 */
export const ORDER_BULK_MAX = 250
/** Câu báo khi lô vượt `ORDER_BULK_MAX` — có ": " nên bộ kiểm lược đồ trả nguyên câu, không kèm tên trường. */
export const BULK_TOO_MANY = `Đang chọn quá ${String(ORDER_BULK_MAX)} đơn: mỗi lượt làm tối đa ${String(ORDER_BULK_MAX)} đơn — bỏ bớt ô tick rồi làm lần lượt từng lô.`

/**
 * TRẦN SỐ DÒNG một lần dán của ba hộp nhập hàng loạt (rà 16/10 L40 — lỗi riêng rs-erp: bản cũ ghi thẳng vào kho trình duyệt
 * nên không có trần). Màn soát TRƯỚC khi gửi và báo tiếng Việt kèm cách chia lô; máy chủ vẫn giữ đúng trần này.
 */
export const BULK_ORDER_IMPORT_MAX = 500
export const BULK_REVENUE_MAX = 1000
export const BULK_COST_IMPORT_MAX = 500

export interface OrderFilters {
  quick: QuickKey
  /** Ô 1 — mã đơn / mã cont. DÁN CẢ CỘT từ Excel được: mỗi dòng một mã. */
  q: string
  /** Ô 2 — tên khách / MST. Tách khỏi ô 1 (bản cũ user 25/08) vì gõ nhiều từ ≠ dán nhiều mã. */
  qName: string
  routes: string[]
  orderStatuses: string[]
  packs: string[]
  contTypes: string[]
  customers: string[]
  billStatuses: string[]
  sales: string[]
  /** Chuyến đi: `yes` đã bắn cho ≥ 1 thầu phụ · `no` chưa. */
  trip: '' | 'yes' | 'no'
  /** Thu tiền khách: `paid` đã thanh toán đủ · `unpaid` chưa (kể cả chưa có hóa đơn). */
  paid: '' | 'paid' | 'unpaid'
  /** Trả NCC: `owe` còn khoản chưa chi · `clear` đã chi hết / chưa có khoản. */
  owe: '' | 'owe' | 'clear'
  /** MST của KHÁCH. */
  msts: string[]
  /** Đối tác (thầu phụ) đã bắn chặng. */
  partners: string[]
  /**
   * ĐỐI TÁC KẾ HOẠCH = thầu phụ dự kiến của đơn (`plannedVendorId`, ô "Đối tác dự kiến" ở form đơn) — CHỈ kế hoạch, khác
   * `partners` (NCC đã bắn chặng). `''` = đơn chưa có đối tác kế hoạch. Yêu cầu mới của anh (30/10/2026): màn Đơn hàng bản cũ
   * không có ô này; màn Điều hành bản cũ có ô cùng nghĩa (`fPlannedVendor`, `Az.tsx:7633`, `'__none__'` = chưa có).
   */
  plannedVendors: string[]
  /** Bảng kê MUA: `none` còn khoản chưa lập · `has` đã lập hết · `nocost` chưa nhập giá vốn. */
  stmts: string[]
  /** Nguồn đơn — xem `ORDER_SOURCES`. */
  source: OrderSource
  dateBasis: DateBasis
  from: string
  to: string
}

export const EMPTY_FILTERS: OrderFilters = {
  quick: 'all', q: '', qName: '',
  routes: [], orderStatuses: [], packs: [], contTypes: [], customers: [], billStatuses: [],
  sales: [], trip: '', paid: '', owe: '', msts: [], partners: [], plannedVendors: [], stmts: [], source: '',
  dateBasis: 'planPickup', from: '', to: '',
}

/**
 * ⭐ MỞ MÀN RA LÀ ĐANG Ở THÁNG HIỆN TẠI (bản cũ `Az.tsx:1655`, chốt 24/07 sau khi để trắng một thời gian).
 *
 * Để trắng thì mở màn là kéo TOÀN BỘ lịch sử đơn: danh sách dài, và mọi ô KPI + chip đếm nhanh đều
 * tính trên cả kho chứ không phải kỳ đang làm — người dùng đọc ra những con số không phải của tháng này.
 *
 * ⚠ `EMPTY_FILTERS` GIỮ NGUYÊN rỗng: nó là mốc để biết "người dùng có đang lọc gì không"
 *   (`hasAnyFilter`) và là thứ nút ✕ Xóa lọc trả về — xóa lọc nghĩa là xem hết, đúng ý người bấm.
 */
export const monthFilters = (todayIso: string): OrderFilters => {
  const [y = '', m = ''] = todayIso.split('-')
  const last = new Date(Number(y), Number(m), 0).getDate()
  return { ...EMPTY_FILTERS, from: `${y}-${m}-01`, to: `${y}-${m}-${String(last).padStart(2, '0')}` }
}

export const hasAnyFilter = (f: OrderFilters): boolean =>
  f.quick !== 'all' || f.q.trim() !== '' || f.qName.trim() !== ''
  || f.routes.length > 0 || f.orderStatuses.length > 0 || f.packs.length > 0
  || f.contTypes.length > 0 || f.customers.length > 0 || f.billStatuses.length > 0
  || f.sales.length > 0 || f.from !== '' || f.to !== ''
  || f.trip !== '' || f.paid !== '' || f.owe !== '' || f.msts.length > 0 || f.partners.length > 0 || f.stmts.length > 0
  || f.plannedVendors.length > 0 || f.source !== ''

/** So mã thì bỏ hết ký tự không phải chữ-số: người dùng dán mã có dấu cách, gạch, xuống dòng. */
export const normCode = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '')

/**
 * Tách ô tìm thành danh sách mã. Một mã ⇒ tìm MỜ; nhiều mã ⇒ khớp ĐÚNG từng mã.
 * ⭐ Y bản cũ `qList` (`Az.tsx:1610`; nhap-loat-10): tách cả theo `|`, chữ thường, BỎ TRÙNG — dán 12 dòng có 2 mã lặp thì báo
 *   "Đã dán 10 mã", và danh sách mã thiếu không lặp.
 */
export const codeList = (q: string): string[] =>
  [...new Set(q.split(/[\s,;|]+/).map((s) => s.trim().toLowerCase()).filter((s) => s !== ''))]

/**
 * Đúng những ô của một dòng đơn mà bộ lọc đọc tới. `OrderDto` (web) và `Order` (`@rs/order`) đều khớp khuôn này.
 * ⚠ `| undefined` tường minh ở ô tuỳ chọn: kho bật `exactOptionalPropertyTypes`, và `legacy` của `OrderDto` là
 *   `.optional()` (bản hợp đồng cũ không có nó).
 */
export interface BoardOrder {
  ref: string
  contNo: string | null
  customerId: string
  routeName: string | null
  orderStatus: string
  packId: string | null
  contType: string | null
  billStatus?: string | null | undefined
  ownerUserId: string | null
  planDate: string
  dueAt: string | null
  pickups: readonly unknown[] | null
  drops: readonly unknown[] | null
  createdAt: string
  draft?: boolean | undefined
  legacy?: boolean | undefined
  /** Thầu phụ dự kiến (`plannedVendorId`) — ô lọc "Đối tác kế hoạch". Vắng / `null` = chưa có. */
  plannedVendorId?: string | null | undefined
}

/** Khách — đúng ba ô mà ô "tên khách / MST" và ô MST đọc tới. */
export interface BoardCustomer {
  id: string
  name: string
  code: string | null
  tax: string | null
  /** SALE CHÍNH của khách — bộ lọc "NVKD" gom theo đây (bản cũ `Az.tsx:1640`). Vắng = chưa gán. */
  salesMainId?: string | null | undefined
}

/**
 * Cont LẠNH. Bản cũ nhận diện bằng chính chữ người dùng gõ vào ô loại cont (`Az.tsx` `isReefer`) —
 * không có cờ riêng trong dữ liệu, và ba cách viết đều gặp thật: `RF`, `lạnh`, `lanh`.
 */
export const isReefer = (o: { contType: string | null }): boolean => /rf|lạnh|lanh/i.test(o.contType ?? '')

/**
 * Trạng thái TIỀN của một đơn, suy từ hóa đơn AR gắn với nó.
 * `null` = KHÔNG BIẾT — đơn chưa có hóa đơn, hoặc người đang xem không đọc được sổ công nợ.
 */
export type PayState = 'paid' | 'partial' | 'unpaid'
export type PaidLookup<T extends BoardOrder = BoardOrder> = (o: T) => PayState | null

/**
 * Vế CHIP của một đơn — CHƯA xét nháp (bản cũ `quickPass`, `Az.tsx:1572-1584`).
 *
 * ⭐ "Chờ vận hành" và "Đang chạy" SUY TỪ CHẶNG như bản cũ (kiểm kê 23/10, R0666 · R0667):
 *   · `noleg` — đơn không còn chặng SỐNG nào (`azLegProgress(...).total === 0`, chặng NCC từ chối không tính);
 *   · `running` — có chặng sống mà chưa giao hết (`azJobStatusOf === 'running'`).
 *   Trước 23/10 hai chip đọc `orderStatus` với lý lẽ "cùng một câu trả lời" — sai: bắn chặng xong máy chủ tự chuyển đơn
 *   sang "Đã xác nhận" nên đơn đã có chặng vẫn nằm ở Chờ vận hành, còn đơn "Đã lấy hàng" không vào chip nào.
 * ⚠ Bảng tóm tắt chưa về (`null`) ⇒ coi như chưa có chặng.
 */
const quickPass = <T extends BoardOrder>(
  o: T, quick: QuickKey, paidOf: PaidLookup<T>, boardOf: ((o: T) => BoardFacts | null) | undefined,
): boolean => {
  const today = toISODate()
  switch (quick) {
    case 'today': return o.planDate === today
    case 'tomorrow': return o.planDate === addDaysIso(today, 1)
    case 'noleg': return (boardOf?.(o)?.liveLegs ?? 0) === 0
    case 'running': {
      const b = boardOf?.(o) ?? null
      return b !== null && b.liveLegs > 0 && b.doneLegs < b.liveLegs
    }
    case 'unbilled': return (o.billStatus ?? 'none') !== 'issued'
    case 'owing': { const p = paidOf(o); return p === 'unpaid' || p === 'partial' }
    case 'reefer': return isReefer(o)
    default: return true
  }
}

/**
 * Một đơn có HIỆN dưới chip đang chọn không.
 * ⭐ LỌC NHÁP TRƯỚC MỌI CHIP (bản cũ `Az.tsx:1614`): chip "Nháp" chỉ lấy đơn nháp, MỌI chip khác đều BỎ đơn nháp
 *   (kiểm kê 23/10, R0364–R0366 — trước đó chỉ chip "Tất cả" bỏ nháp, các chip khác lẫn đơn nháp vào danh sách).
 */
export const matchQuick = <T extends BoardOrder>(
  o: T, quick: QuickKey, paidOf: PaidLookup<T>, boardOf?: (o: T) => BoardFacts | null,
): boolean =>
  (quick === 'draft' ? o.draft === true : o.draft !== true) && quickPass(o, quick, paidOf, boardOf)

/**
 * Số trên MỘT chip — khác danh sách: chip đếm trên `base` CẢ đơn nháp, trừ "Tất cả" (bỏ nháp) và "Nháp" (chỉ nháp)
 * (bản cũ `QUICKS`, `Az.tsx:2260-2270`).
 */
const countsUnder = <T extends BoardOrder>(
  o: T, quick: QuickKey, paidOf: PaidLookup<T>, boardOf: ((o: T) => BoardFacts | null) | undefined,
): boolean => {
  if (quick === 'all') return o.draft !== true
  if (quick === 'draft') return o.draft === true
  return quickPass(o, quick, paidOf, boardOf)
}

/**
 * ══ THU TIỀN / NGÀY XUẤT HĐ CỦA MỘT ĐƠN — LUẬT THUẦN DUY NHẤT (kiểm kê 23/10, R0337 · R0340 · R0356 · R0382 · R0670 · R1326 · R1345) ══
 * Chép `azFifoPay` · `azBillState` · `azInvDateOfJob` (`proship-trans/src/lib/azInvoice.ts:95`, `:153`, `:145`).
 * ⭐ MỘT chỗ cho cả bảng Đơn hàng (bản gốc đối chứng của SQL `board-store`) lẫn Chi tiết đơn (`@rs/ar` `jobPayState` gọi lại
 *   `billPayOf`) — hai màn không được ra hai trạng thái thu tiền cho cùng một đơn.
 * ⚠ Hàng đợi FIFO gom dòng theo CÔNG TRÌNH = MÃ ĐƠN (`congTrinh`, bản cũ khoá `''` cho dòng không ghi mã) — không theo id đơn:
 *   dòng hoá đơn lập từ Sổ chứng từ bán chỉ có mã (`job_ref`), không có `job_id`.
 */
export interface FifoInvoice {
  id: string
  status: string
  paidVnd: number
  /** Dòng theo THỨ TỰ in: công trình (mã đơn) + tiền dòng GỒM VAT. */
  lines: readonly { jobRef: string | null; lineVnd: number }[]
}

export interface BoardInvoice extends FifoInvoice {
  /** `YYYY-MM-DD`. */
  docDate: string
  invoiceDate: string | null
  /** Hoá đơn lập cho đúng một đơn (`azJobId`). */
  jobId: string | null
  /** Chuỗi mã đơn (nhiều đơn ngăn bằng dấu phẩy, `azJobRef`). */
  jobRef: string | null
}

/**
 * FIFO số đã thu của MỘT tờ xuống các đơn theo thứ tự dòng (`azFifoPay`): trả đủ (`status = 'paid'`) ⇒ mọi đơn đã thu; trả
 * một phần ⇒ lấp đầy TRÊN → XUỐNG, đơn ranh giới "một phần". Dòng gom theo công trình, thứ tự lần gặp đầu.
 */
export const fifoPayOf = (ref: string, inv: FifoInvoice): PayState => {
  if (inv.status === 'paid') return 'paid'
  if (!(inv.paidVnd > 0)) return 'unpaid'
  const amtByRef = new Map<string, number>()
  for (const l of inv.lines) { const k = l.jobRef ?? ''; amtByRef.set(k, (amtByRef.get(k) ?? 0) + l.lineVnd) }
  let running = 0
  for (const [k, amt] of amtByRef) {
    if (k === ref) return inv.paidVnd >= running + amt ? 'paid' : inv.paidVnd > running ? 'partial' : 'unpaid'
    running += amt
  }
  return 'unpaid'
}

/**
 * Trạng thái thu tiền của ĐƠN (`azBillState().pay`): tờ #1 (`arInvoiceId`) theo FIFO; đơn TÁCH 2 HĐ phải thu đủ CẢ tờ #2.
 * `null` = chưa gắn tờ nào thấy được ("none" của bản cũ — màn hiểu là "không biết").
 */
export const billPayOf = (
  o: { ref: string; arInvoiceId: string | null; arInvoiceId2?: string | null | undefined },
  invoices: readonly FifoInvoice[],
): PayState | null => {
  const ar = o.arInvoiceId === null ? undefined : invoices.find((a) => a.id === o.arInvoiceId)
  const p1 = ar === undefined ? null : fifoPayOf(o.ref, ar)
  const ar2 = (o.arInvoiceId2 ?? null) === null ? undefined : invoices.find((a) => a.id === o.arInvoiceId2)
  if (ar2 === undefined) return p1
  const p2 = fifoPayOf(o.ref, ar2)
  return p1 === 'paid' && p2 === 'paid' ? 'paid' : p1 === 'unpaid' && p2 === 'unpaid' ? 'unpaid' : 'partial'
}

/**
 * NGÀY XUẤT HĐ của đơn (`azInvDateOfJob`): dò MỌI tờ KHÔNG HUỶ của đơn — tờ #1 · tờ #2 · `jobId` · chuỗi `jobRef` · công trình
 * từng dòng (`azArsOfJob`) — lấy `invoiceDate || docDate` SỚM NHẤT. Không có tờ nào ⇒ `null`.
 */
export const invoiceDateOf = (
  o: { id: string; ref: string; arInvoiceId: string | null; arInvoiceId2?: string | null | undefined },
  invoices: readonly BoardInvoice[],
): string | null => {
  const ref = o.ref.trim()
  const ids = new Set([o.arInvoiceId, o.arInvoiceId2 ?? null].filter((x): x is string => x !== null))
  const refsOf = (a: BoardInvoice): string[] => (a.jobRef ?? '').split(',').map((x) => x.trim()).filter((x) => x !== '')
  const mine = invoices.filter((a) => a.status !== 'cancelled' && (
    ids.has(a.id) || a.jobId === o.id
    || (ref !== '' && refsOf(a).includes(ref))
    || (ref !== '' && a.lines.some((l) => (l.jobRef ?? '').trim() === ref))))
  return mine.map((a) => (a.invoiceDate ?? a.docDate).slice(0, 10)).filter((d) => d !== '').sort()[0] ?? null
}

/** Ngày của tờ hóa đơn AR gắn với đơn — `null` khi chưa xuất hoặc không đọc được sổ. */
export type InvoiceDateLookup<T extends BoardOrder = BoardOrder> = (o: T) => string | null

/** Sự thật vận hành / giá vốn của một đơn — từ bảng tóm tắt điều hành (`POST /az/dieu-hanh/tom-tat`). */
export interface BoardFacts {
  trips: number
  vendorIds: readonly string[]
  /**
   * ĐỐI TÁC của đơn = NCC trên MỌI chặng, KỂ CẢ chặng NCC đã từ chối (bản cũ `jobPartners`, `Az.tsx:1595` — NccPortal từ chối
   * chỉ bật cờ, giữ `vendorId`). Ô lọc "Đối tác" và danh sách chọn của nó đọc tập này (kiểm kê 23/10, R0342 · R0390).
   */
  partnerIds: readonly string[]
  /** Số chặng SỐNG (bỏ chặng NCC từ chối) và số chặng sống đã giao (`azLegProgress`) — chip "Chờ vận hành" · "Đang chạy". */
  liveLegs: number
  doneLegs: number
  /** `null` khi người xem không được thấy giá vốn. */
  cost: {
    costCount: number; confirmedCount: number; unpaidCount: number; stmtState: '' | 'none' | 'has'
    /**
     * Trạng thái TRẢ NCC (`azVendorPayState`, `proship-trans/src/lib/azCost.ts:127`): chia số đã trả của từng chứng từ mua FIFO
     * theo thứ tự khoản (mới → cũ như kho bản cũ). Ô lọc "Trả NCC": còn nợ = `unpaid`/`partial`, đã trả = `paid`/`none`.
     */
    vendorPay: 'none' | 'unpaid' | 'partial' | 'paid'
  } | null
}

export interface FilterDeps<T extends BoardOrder = BoardOrder> {
  customers: readonly BoardCustomer[]
  /** `null` khi chưa tải xong bảng tóm tắt — lọc theo chuyến / đối tác / NCC / bảng kê khi đó không loại đơn nào. */
  boardOf?: (o: T) => BoardFacts | null
  /** Trạng thái tiền của đơn — `() => null` khi không đọc được sổ công nợ. */
  paidOf: PaidLookup<T>
  /** Ngày hóa đơn — chỉ dùng khi lọc theo mốc `invoiced`. */
  invoicedOf: InvoiceDateLookup<T>
}

/** Ngày đem ra so, theo mốc người dùng chọn. `createdAt` là dấu thời gian ⇒ cắt lấy 10 ký tự. */
const stopField = (stops: readonly unknown[] | null, last: boolean, field: 'at' | 'actualAt'): string => {
  const list = (stops ?? []) as Record<string, unknown>[]
  const s = last ? list[list.length - 1] : list[0]
  const v = s?.[field]
  return typeof v === 'string' ? v.slice(0, 10) : ''
}
/**
 * ⭐ NGÀY LẤY KH = ngày của `pickups[0].at`, thiếu mới lấy `planDate` (bản cũ `azStopDates`, `azSales.ts:223`; mốc lọc mặc định
 *   `basisDate`, `Az.tsx:1591` — anh chốt 23/10, R0047). Sửa ngày thực hiện trên bảng kê mua đổi `plan_date` mà không chạm `at`
 *   (`patchOrderFromStatement`, bản cũ `applyStmtDates` `Az.tsx:26998-27004`) ⇒ bộ lọc tháng vẫn xếp đơn theo ngày cũ như bản cũ.
 *   "Bốc thực tế" thiếu `actualAt` thì lùi về CHÍNH ngày lấy KH này (`azSales.ts:225`).
 */
const planPickupOf = (o: BoardOrder): string => stopField(o.pickups, false, 'at') || o.planDate
const dateOf = <T extends BoardOrder>(o: T, basis: DateBasis, invoicedOf: InvoiceDateLookup<T>): string => {
  if (basis === 'planDeliver') return o.dueAt ?? ''
  if (basis === 'actualPickup') return stopField(o.pickups, false, 'actualAt') || planPickupOf(o)
  if (basis === 'actualDeliver') return stopField(o.drops, true, 'actualAt') || (o.dueAt ?? '')
  if (basis === 'created') return o.createdAt.slice(0, 10)
  if (basis === 'invoiced') return invoicedOf(o) ?? ''
  return planPickupOf(o)
}

export const filterOrders = <T extends BoardOrder>(
  rows: readonly T[], f: OrderFilters, deps: FilterDeps<T>,
): T[] => {
  const cusOf = (id: string): BoardCustomer | undefined => deps.customers.find((c) => c.id === id)
  const codes = codeList(f.q)
  const qName = f.qName.trim().toLowerCase()

  const showCancelled = showsCancelled(f)

  return rows.filter((o) => {
    if (o.orderStatus === 'cancelled' && !showCancelled) return false
    if (!matchQuick(o, f.quick, deps.paidOf, deps.boardOf)) return false
    if (f.source !== '' && (o.legacy === true) !== (f.source === 'legacy')) return false

    if (codes.length === 1) {
      // MỘT mã ⇒ tìm MỜ: người dùng đang gõ dở, chưa chắc nhớ hết mã.
      const needle = normCode(codes[0] as string)
      const hay = `${normCode(o.ref)} ${normCode(o.contNo ?? '')}`
      if (!hay.includes(needle)) return false
    } else if (codes.length > 1) {
      // NHIỀU mã (dán từ Excel) ⇒ khớp ĐÚNG: tìm mờ ở đây sẽ kéo về những đơn không ai hỏi.
      // ⚠ Số cont chỉ đem so khi đơn CÓ số cont (bản cũ `!!j.contNo && …`, `Az.tsx:1624`; kiểm kê 23/10 R0371): ô dán có "-"
      //   hay "/" chuẩn hoá ra rỗng, không chặn thì kéo về MỌI đơn chưa có số cont.
      const want = new Set(codes.map(normCode))
      const contHit = (o.contNo ?? '') !== '' && want.has(normCode(o.contNo ?? ''))
      if (!want.has(normCode(o.ref)) && !contHit) return false
    }

    if (qName !== '') {
      const c = cusOf(o.customerId)
      const hay = `${c?.name ?? ''} ${c?.code ?? ''} ${c?.tax ?? ''}`.toLowerCase()
      if (!hay.includes(qName)) return false
    }

    if (f.routes.length > 0 && !f.routes.includes(o.routeName ?? '')) return false
    if (f.orderStatuses.length > 0 && !f.orderStatuses.includes(o.orderStatus)) return false
    if (f.packs.length > 0 && !f.packs.includes(o.packId ?? '')) return false
    if (f.contTypes.length > 0 && !f.contTypes.includes(o.contType ?? '')) return false
    if (f.customers.length > 0 && !f.customers.includes(o.customerId)) return false
    if (f.billStatuses.length > 0 && !f.billStatuses.includes(o.billStatus ?? 'none')) return false
    // "NVKD" = SALE CHÍNH CỦA KHÁCH (bản cũ `Az.tsx:1640` `custOf(j.customerId)?.salesMainId`), KHÔNG phải người tạo đơn.
    if (f.sales.length > 0 && !f.sales.includes(cusOf(o.customerId)?.salesMainId ?? '')) return false
    if (f.msts.length > 0 && !f.msts.includes(cusOf(o.customerId)?.tax ?? '')) return false
    // Đối tác kế hoạch: đọc ngay trên đơn, không cần bảng tóm tắt điều hành; chưa có ⇒ `''` (mục "chưa có").
    if (f.plannedVendors.length > 0 && !f.plannedVendors.includes(o.plannedVendorId ?? '')) return false
    if (f.paid !== '') {
      const p = deps.paidOf(o)
      if ((p === 'paid') !== (f.paid === 'paid')) return false
    }
    const b = deps.boardOf?.(o) ?? null
    if (b !== null) {
      if (f.trip !== '' && (b.trips > 0) !== (f.trip === 'yes')) return false
      if (f.partners.length > 0 && !f.partners.some((v) => b.partnerIds.includes(v))) return false
      if (b.cost !== null) {
        // Bản cũ `Az.tsx:1635` — R0355 · R0383–R0385.
        if (f.owe !== '' && (b.cost.vendorPay === 'unpaid' || b.cost.vendorPay === 'partial') !== (f.owe === 'owe')) return false
        const st = b.cost.stmtState === '' ? 'nocost' : b.cost.stmtState
        if (f.stmts.length > 0 && !f.stmts.includes(st)) return false
      }
    }

    const d = dateOf(o, f.dateBasis, deps.invoicedOf)
    if (f.from !== '' && (d === '' || d < f.from)) return false
    if (f.to !== '' && (d === '' || d > f.to)) return false
    return true
  })
}

/**
 * ĐƠN ĐÃ HUỶ ẩn khỏi `base`, trừ khi lọc trạng thái "Đã huỷ" hoặc đang gõ một trong hai ô tìm (bản cũ `Az.tsx:1567-1568`).
 */
export const showsCancelled = (f: Pick<OrderFilters, 'orderStatuses' | 'q' | 'qName'>): boolean =>
  f.orderStatuses.includes('cancelled') || f.q.trim() !== '' || f.qName.trim() !== ''

/** `base` của màn: đơn trong phạm vi, bỏ đơn huỷ theo `showsCancelled` — GỒM đơn nháp. */
export const baseOrders = <T extends BoardOrder>(rows: readonly T[], f: OrderFilters): T[] => {
  const showCancelled = showsCancelled(f)
  return rows.filter((o) => showCancelled || o.orderStatus !== 'cancelled')
}

/**
 * Số trên từng chip lọc nhanh.
 *
 * ⚠ ĐẾM TRÊN `base`, KHÔNG phải trên kết quả đang lọc — đúng bản cũ. Con số ở đây trả lời
 *   *"bấm vào sẽ thấy bao nhiêu đơn"*; đếm trên kết quả đã lọc thì mọi chip khác đều hiện 0 và
 *   người dùng tưởng không còn gì.
 * ⚠ `base` bỏ đơn huỷ THEO BỘ LỌC (`showsCancelled`): đang tìm hoặc lọc "Đã huỷ" thì chip đếm cả đơn huỷ (kiểm kê 23/10,
 *   R0662 — trước đó luôn bỏ).
 */
export const quickCounts = <T extends BoardOrder>(
  rows: readonly T[], f: OrderFilters, paidOf: PaidLookup<T>, boardOf?: (o: T) => BoardFacts | null,
): Record<QuickKey, number> => {
  const out = {} as Record<QuickKey, number>
  /* ⛔ KHÔNG ĐẾM ĐƠN HỆ CŨ (anh chốt 21/10: "không cần cộng đơn hệ cũ"). Bản cũ không có đơn hệ cũ — chip của nó đếm đơn của
     chính hệ thống; đơn di trú RATRACO cũ là phần rs-erp mới phát sinh, cộng vào làm "Tất cả 13.120 · Cont lạnh 1.999" vô nghĩa
     với việc hằng ngày. Đơn cũ vẫn xem / lọc được (ô "Nguồn đơn"), chỉ không vào số trên chip. */
  const own = baseOrders(rows, f).filter((o) => o.legacy !== true)
  for (const k of QUICK_KEYS) out[k] = own.filter((o) => countsUnder(o, k, paidOf, boardOf)).length
  return out
}

/**
 * Danh sách chọn của ô "Đối tác": NCC trên MỌI chặng (kể cả bị từ chối) của các đơn trong `base` — bỏ đơn huỷ theo bộ lọc,
 * GỒM đơn nháp (bản cũ `partnerOptions`, `Az.tsx:1657`; kiểm kê 23/10 R0405 · R0406). Sắp theo id.
 */
export const partnerOptionIds = <T extends BoardOrder>(
  rows: readonly T[], f: OrderFilters, boardOf: (o: T) => BoardFacts | null,
): string[] => [...new Set(baseOrders(rows, f).flatMap((o) => boardOf(o)?.partnerIds ?? []))].sort()

/**
 * Danh sách chọn của ô "Đối tác kế hoạch": thầu phụ dự kiến (khác rỗng) của các đơn trong `base` — cùng tập đơn với
 * `partnerOptionIds` (bỏ đơn huỷ theo bộ lọc, GỒM nháp). Mục "chưa có" (`''`) màn tự thêm. Sắp theo id.
 */
export const plannedVendorOptionIds = <T extends BoardOrder>(rows: readonly T[], f: OrderFilters): string[] =>
  [...new Set(baseOrders(rows, f).map((o) => o.plannedVendorId ?? '').filter((v) => v !== ''))].sort()

/**
 * Dán NHIỀU mã: bao nhiêu mã, thiếu mã nào trong danh sách ĐANG LỌC (bản cũ `Az.tsx:2303`). Một mã (đang gõ dở) ⇒ `null`.
 */
export const pastedCodes = (
  q: string, rows: readonly { ref: string; contNo: string | null }[],
): { pasted: number; missing: string[] } | null => {
  const codes = codeList(q)
  if (codes.length <= 1) return null
  const found = new Set(rows.flatMap((o) => [normCode(o.ref), normCode(o.contNo ?? '')].filter((x) => x !== '')))
  return { pasted: codes.length, missing: codes.filter((c) => !found.has(normCode(c))) }
}

/* ══ KPI NHANH CỦA DANH SÁCH ĐƠN — luật thuần (chép dải `az.kpi.*` của bản cũ) ════════════════════════════════
 *
 * ⭐ ĐỌC TRÊN DANH SÁCH ĐANG LỌC. Sáu con số này trả lời *"cái tôi đang nhìn đang chạy thế nào"* —
 *   lọc một khách, một tuyến, một tháng thì KPI phải đi theo. Một dải KPI tính trên cả kho không
 *   đổi khi người dùng lọc, và khi đó nó chỉ là trang trí.
 *
 * ⚠ TIỀN CHỈ HIỆN VỚI NGƯỜI XEM ĐƯỢC GIÁ VỐN. `costVnd`/`profitVnd` do máy chủ trả `null` cho
 *   người khác (`OpsBoardDto.cost` nullable) — ở đây quy về `null`, KHÔNG quy về 0: "không được
 *   xem" và "bằng 0" là hai chuyện khác nhau.
 */

/** Đúng những ô của một dòng đơn mà dải KPI đọc tới. */
export interface OrderForKpi {
  orderStatus: string
  /** Hạn giao cam kết (`YYYY-MM-DD`); `null` = không cam kết hạn. */
  dueAt: string | null
  sellPrice: string
}

/** Phần tóm tắt vận hành của một đơn — `null` khi máy chủ chưa trả (hoặc không được xem giá vốn). */
export interface BoardForKpi {
  legCount: number
  costVnd: number | null
  unpaidVnd: number | null
}

export interface OrderKpi {
  /** Đơn đã quá hạn giao mà CHƯA giao xong. */
  lateCount: number
  /** Đơn đang trên đường (đã bắt đầu, chưa giao, chưa huỷ). */
  runningCount: number
  /** Đơn CHƯA có chặng nào — chưa giao cho nhà thầu. */
  noLegCount: number
  sellVnd: number
  /** `null` ⇒ người xem không được thấy giá vốn. */
  buyVnd: number | null
  profitVnd: number | null
  /** Biên lãi gộp theo %, một chữ số. `null` khi chưa biết giá vốn hoặc chưa có doanh thu. */
  marginPct: number | null
  unpaidVnd: number | null
}

const DONE: readonly string[] = ['delivered', 'cancelled']
/** Đơn đã rời bậc "mới nhận" nhưng chưa xong — đúng dải trạng thái bản cũ gọi là "đang chạy". */
const RUNNING: readonly string[] = ['picking', 'in_transit', 'at_drop']

export const orderKpi = (
  rows: readonly OrderForKpi[], boardOf: (i: number) => BoardForKpi | null, todayIso: string,
): OrderKpi => {
  let sellVnd = 0
  let buyVnd = 0
  let unpaidVnd = 0
  let costKnown = false
  let lateCount = 0
  let runningCount = 0
  let noLegCount = 0

  rows.forEach((o, i) => {
    /* ⛔ Đơn ĐÃ HUỶ không cộng vào tiền (anh báo 23/10): tìm theo mã thì đơn huỷ vẫn HIỆN trong bảng, nhưng dải Doanh thu ·
       Chi phí · Lãi chỉ tính đơn còn sống. */
    if (o.orderStatus === 'cancelled') return
    sellVnd += Number(o.sellPrice) || 0
    if (o.dueAt !== null && o.dueAt < todayIso && !DONE.includes(o.orderStatus)) lateCount += 1
    if (RUNNING.includes(o.orderStatus)) runningCount += 1
    const b = boardOf(i)
    if (b !== null && b.legCount === 0 && o.orderStatus !== 'cancelled') noLegCount += 1
    if (b?.costVnd != null) { costKnown = true; buyVnd += b.costVnd }
    if (b?.unpaidVnd != null) unpaidVnd += b.unpaidVnd
  })

  const profitVnd = costKnown ? sellVnd - buyVnd : null
  return {
    lateCount, runningCount, noLegCount, sellVnd,
    buyVnd: costKnown ? buyVnd : null,
    profitVnd,
    marginPct: profitVnd === null || sellVnd <= 0 ? null : Math.round((profitVnd / sellVnd) * 1000) / 10,
    unpaidVnd: costKnown ? unpaidVnd : null,
  }
}

/**
 * THU HỘ của danh sách đang lọc (bản cũ `obTotal`, `Az.tsx:2831`) — nâng hạ · cầu đường chi hộ
 * khách. Khách vẫn trả, nhưng KHÔNG phải doanh thu và KHÔNG chịu VAT, nên đứng thành một ô
 * riêng chứ không cộng vào ô Doanh thu. (Dời từ thân `OrdersTab` ra đây để có bản gốc đối chứng.)
 */
export const onBehalfTotal = (rows: readonly { revenues: readonly unknown[] | null; orderStatus?: string }[]): number =>
  rows.reduce((sum, o) => o.orderStatus === 'cancelled' ? sum : sum + (o.revenues ?? []).reduce<number>((s, line) => {
    const l = line as Record<string, unknown>
    if (l['kind'] !== 'onbehalf') return s
    const amt = Number(l['amount'])
    return s + (Number.isFinite(amt) ? amt : 0)
  }, 0), 0)
